import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { getFolder, resolveFolderPath } from "../../services/folder.service.js";
import FolderMeta from "../../components/visualizationsPage/FolderMeta";
import ViewSwitcher from "../../components/visualizationsPage/ViewSwitcher";
import EntriesSection from "../../components/visualizationsPage/EntriesSection";
import PathBar from "../../components/visualizationsPage/PathBar.jsx";
import "../../styles/visualization/visualizationsPage.css";

export default function VisualizationsPage() {
  const ROOT_FOLDER_ID = import.meta.env.VITE_VISUALIZATIONS_FOLDER_ID;
  const { "*": fullPath } = useParams();

  const folderIds = useMemo(() => {
    const parts = fullPath?.split("/").filter(Boolean) || [];
    return [ROOT_FOLDER_ID, ...parts];
  }, [fullPath]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(
    localStorage.getItem("viz_view_mode") || "grid",
  );
  const [folderPath, setFolderPath] = useState([]);

  useEffect(() => {
    localStorage.setItem("viz_view_mode", viewMode);
  }, [viewMode]);

  useEffect(() => {
    let active = true;
    if (!folderIds?.length) return;

    setLoading(true);

    resolveFolderPath({ path: folderIds }).then((res) => {
      if (active) setFolderPath(res);
    });

    getFolder(folderIds[folderIds.length - 1], { page: 1, limit: 200 }).then(
      (res) => {
        if (!active) return;
        setData(res);
        setLoading(false);
      },
    );

    return () => (active = false);
  }, [folderIds]);

  /* ================= SEO: Collection Schema ================= */
  useEffect(() => {
    if (!data || !folderPath.length) return;

    // Remove old one safely
    const old = document.querySelector('script[data-seo="collection"]');
    if (old) old.remove();

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.dataset.seo = "collection";
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: data.folderMetaData.title,
      description:
        data.folderMetaData.description ||
        "Explore interactive visualizations on VisualLab.",
      url: window.location.origin + window.location.pathname,
      hasPart: data.folderEntries.records.map((entry) => ({
        "@type": "LearningResource",
        name: entry.item.title,
        url: `${window.location.origin}/f/visualizations/${entry.itemId}`,
      })),
    });

    document.head.appendChild(script);

    return () => {
      const node = document.querySelector('script[data-seo="collection"]');
      if (node) node.remove();
    };
  }, [data, folderPath]);

  /* ================= SEO: Meta Tags ================= */
  useEffect(() => {
    if (!data) return;

    const title = `${data.folderMetaData.title} Visualizations | VisualLab`;
    document.title = title;

    const ensureMeta = (selector, attr, value) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        Object.entries(attr).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      }
      el.content = value;
    };

    ensureMeta(
      'meta[name="description"]',
      { name: "description" },
      data.folderMetaData.description ||
        "Browse interactive visual explanations on VisualLab.",
    );

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = window.location.origin + window.location.pathname;

    ensureMeta('meta[property="og:title"]', { property: "og:title" }, title);
    ensureMeta(
      'meta[property="og:description"]',
      { property: "og:description" },
      data.folderMetaData.description ||
        "Explore interactive visualizations on VisualLab.",
    );
    ensureMeta('meta[property="og:type"]', { property: "og:type" }, "website");
    ensureMeta(
      'meta[property="og:url"]',
      { property: "og:url" },
      window.location.origin + window.location.pathname,
    );
    ensureMeta('meta[name="robots"]', { name: "robots" }, "index, follow");
  }, [data]);

  /* ================= SEO: Breadcrumb Schema ================= */
  useEffect(() => {
    if (!folderPath.length) return;

    const old = document.querySelector('script[data-seo="breadcrumb"]');
    if (old) old.remove();

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.dataset.seo = "breadcrumb";
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: folderPath.map((f, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: f.folderTitle,
        item:
          i === 0
            ? `${window.location.origin}/f/visualizations`
            : `${window.location.origin}/f/visualizations/${folderPath
                .slice(1, i + 1)
                .map((x) => x.folderId)
                .join("/")}`,
      })),
    });

    document.head.appendChild(script);

    return () => {
      const node = document.querySelector('script[data-seo="breadcrumb"]');
      if (node) node.remove();
    };
  }, [folderPath]);

  if (loading || !data) return <div>Loading...</div>;

  return (
    <div className="visualizations-page">
      <PathBar folderPath={folderPath} />
      <FolderMeta
        title={data.folderMetaData.title}
        description={data.folderMetaData.description}
      />
      <ViewSwitcher viewMode={viewMode} setViewMode={setViewMode} />
      <EntriesSection
        entries={data.folderEntries.records}
        viewMode={viewMode}
        loading={loading}
        currentFolderPath={folderIds}
      />
    </div>
  );
}
